import React, { useCallback, useContext, useEffect, useState } from "react";
import classnames from "classnames";
import { getCardsData } from "../../api/getCardsData";
import { getCardsUserData } from "../../api/getCardsUserData";
import { sendPageViewEvent } from "../../utils/utils";
import { AppContext } from "../../AppContext";
import { getDashboardData } from "./api/getDashboardData";
import { SkeletonLoader } from "./components/SkeletonLoader/SkeletonLoader";
import PromoBanner from "./components/PromoBanner";
import GettingStarted from "./components/GettingStarted";
import { FeaturedSlider } from "../../components/FeaturedSlider";
import { EventsSection } from "./components/EventsSection";
import { QuizzesSection } from "./components/QuizzesSection";
import { CardsSection } from "./components/CardsSection";
import {
  setClassesCardsUserData,
  setCoursesCardsUserData,
} from "../../utils/cards";
import { FeaturedSection } from "./components/FeaturedSection";
import { FeaturedSmSection } from "./components/FeaturedSmSection";
import { QuizzesSectionV2 } from "./components/QuizzesSectionV2";
import { ImageBanner } from "./components/ImageBanner";
import "./Dashboard.scss";
import { LiveEventsSection } from "./components/LiveEventsSection";
import QuizAssessment  from "./components/QuizAssessment";

export const Dashboard = () => {
  const { user, json } = useContext(AppContext);
  const { meta } = user;
  const { pc_classic: pcClassic } = meta || {};
  const isPcClassicUser = !!pcClassic;
  const [isLoading, setIsLoading] = useState(true);
  const [state, setState] = useState(null);


  const setClassesSeriesSection = useCallback(async (sections, classes) => {
    const seriesSectionIndex = sections.findIndex(
      (section) => section.type === "cards_class_series"
    );

    if (seriesSectionIndex === -1) {
      return sections;
    }

    const newSections = [...sections];
    newSections[seriesSectionIndex].content = classes.filter(
      (classItem) => classItem.series?.length
    );

    return newSections;
  }, []);

  const setCardsUserData = useCallback(
    async (sections, { courses, classes }) => {
      let classesCardsUserData;
      let newSections = [];

      for (const section of sections) {
        if (
          section.type === "cards_class" ||
          section.type === "cards_class_series"
        ) {
          if (
            typeof section.filter == "object" &&
            section.type === "cards_class"
          ) {
            const classesWithoutSeries = classes.filter(
              (item) => !item.series?.length
            );
            classesCardsUserData = classesCardsUserData || (await getCardsUserData('classes'))
            section.content = await setClassesCardsUserData(
              { type: user.type, isPcClassicUser },
              classesWithoutSeries,
              classesCardsUserData
            );
          } else {
            classesCardsUserData =
              classesCardsUserData || (await getCardsUserData("classes"));
            section.content = await setClassesCardsUserData(
              { type: user.type, isPcClassicUser },
              section.content,
              classesCardsUserData
            );
          }
        }

        if (section.type === "cards_course") {
          if (typeof section.filter == "object") {
            section.content = await setCoursesCardsUserData(user.type, courses);
          } else {
            section.content = await setCoursesCardsUserData(
              user.type,
              section.content
            );
          }
        }

        newSections.push(section);
      }

      return newSections;
    },
    [user.type, isPcClassicUser]
  );

  const init = useCallback(async () => {
    try {
      setIsLoading(true);
      setState(null);

      let data = await getDashboardData();

      const sectionTypes = data.sections.map((item) => item.type);
      const getClasses = getCardsData("classes", json.classes);

      const promises = [getClasses];
      if (sectionTypes?.includes("cards_course")) {
        const getCourses = getCardsData("courses", json.courses);
        promises.push(getCourses);
      }

      const [classes, courses] = await Promise.all(promises);

      data.sections = await setClassesSeriesSection(data.sections, classes);
      data.sections = await setCardsUserData(data.sections, { classes, courses });

      setState(data);
      setIsLoading(false);
    } catch (e) {
      console.error(e);
    }
  }, [setCardsUserData, setClassesSeriesSection, json.classes, json.courses]);

  useEffect(() => {
    const element = document.querySelector("[data-screen='dashboard']");
    const pageTitle =
      element?.getAttribute("data-pagetitle") ||
      "Dashboard – PokerCoaching.com";

    document.title = pageTitle;

    sendPageViewEvent("Dashboard | PokerCoaching.com");
    init();
  }, [init]);

  return (
    <div className={classnames("Dashboard", { Dashboard_loading: isLoading })}>
      {isLoading ? (
        <>
          <SkeletonLoader />
        </>
      ) : (
        <>
          <FeaturedSlider slides={state.slider} />
                                                      
          <div className="Dashboard__body">
            {state.sections.map((section, i) => (
              <RenderSection section={section} state={state} key={i} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

function RenderSection({ section, state }) {
  switch (section.type) {
    case "quiz_assessment":
      return <QuizAssessment sectionData={section} isWidget/>;
    case "webinars":
      return <EventsSection data={section} />;
    case "live_events":
      return <LiveEventsSection data={section} />;
    case "cards_class":
      return <CardsSection data={section} />;
    case "cards_course":
      return <CardsSection data={section} />;
    case "cards_class_series":
      return <CardsSection data={section} />;
    case "quizzes":
      return <QuizzesSection data={section} />;
    case "featured_card":
      return <FeaturedSection data={section} />;
    case "featured_card_sm":
      return <FeaturedSmSection data={section} />;
    case "cards_quizzes":
      return <QuizzesSectionV2 data={section} />;
    case "promoBanner":
      return <PromoBanner banner={state.promoBanner} />;
    case "topBanner":
      return <GettingStarted banner={state.topBanner} />;
    case "image_banner":
      return <ImageBanner banner={section.content} />;
    default:
      return null;
  }
}
